import type { ConnectedUser } from './connection';
import type { StringIndexObject } from './common';

export interface ChatMessage {
	chat_message: number;
	name: string;
	location: string;
	comment: string;
	parent: number;
	email: string | null;
	elevated: number | null;
	approved: number;
	created: string | Date;
	deleted: number;
	image: string;
	first_name: string | null;
	last_name: string | null;
	avatar: string | null;
	replies: ChatMessage[];
	received: Date;
	seen?: boolean;
	id?: number;
}

export interface ChatMessages {
	[key: string]: ChatMessage[];
}

export interface LegacyModeratedChatMessages {
	[key: string]: ChatMessage;
}

export interface Document {
	document: number;
	original_name?: string;
	originalName?: string;
	location: string;
	thumbnail: string | null;
	thumbnail_status: number;
	status: number;
	filesize: number;
}

export interface ChatPost {
	id: number;
	gr_user: number;
	recipient?: ConnectedUser;
	sender?: ConnectedUser;
	meeting: number;
	room?: number;
	created: number | string;
	message: string;
	seen?: boolean;
	isNew?: boolean;
	attachment?: null;
	mentions: number[];
	randomId?: string;
	openTab?: string;
	isOwnMessage?: boolean;
	isChild?: boolean
}

export interface ChatMeta {
	totalMessageCount: number;
}

export interface ChatConvo {
	id?: string;
	messages: ChatPost[];
	meta?: ChatMeta;
}

export interface ChatRoom {
	id: number;
	name: string;
	meeting: number;
	created_by: number;
	participants: number[];
	messages: ChatPost[];
}

export enum EMessageTypes {
	Chat = 'chat',
	Question = 'question',
	QuestionComment = 'question_comment'
}

export enum EStatus {
	Blocked,
	Approved,
	Pending,
	Reported,
	Deleted,
	Unapproved
}

export interface EventsMessageGif {
	title: string;
	url: string;
}

export interface EventsMessageImage {
	name: string;
	url: string;
}

/*
EventsMessage and extended interfaces define the info sent from E3 to GR
For E3 tables:
- `chat_messages`
- `session_questions`
- `question_comments`
*/

export interface EventsMessage {
	id: number;
	bl_profile: number | null;
	first_name: string | null;
	last_name: string | null;
	email: string;
	created_at: number;
	comment: string;
	event: number;
	channel: number;
	status: EStatus;
	blocked_reason: string | null;
	blocked_by: string | null;
	blocked_at: number | null;
	deleted_at: number | null;
	language: string;
	bl_profile_non_registered: number | null;
	spam: boolean | number;
	sent_to_presenter_at: number;
	avatar: string; // Found in profile
	message_type: EMessageTypes;
	seen: boolean;
	sent_to_presenter?: boolean;
	profile?: StringIndexObject<unknown> | null;
	locations?: null;
	session?: number;
}

export interface EventsMessageChat extends EventsMessage {
	reply_to_id: number | null;
	blocked_severity: number | null;
	registration_id: string | null;
	gif: EventsMessageGif | null;
	image: EventsMessageImage | null;
	is_moderator?: boolean;
	uuid?: string | null;
	reply_to?: number | null;
	session_uuid?: string | null;
	user_session?: number | null;
	moderator_profile?: number | null;
}

export interface EventsMessageQuestion extends EventsMessage {
	question: string;
	blocked: boolean | null;
	module_id: number;
}

export interface EventsMessageComment extends EventsMessage {
	likes: number;
	question_id: number;
	blocked: boolean | null;
	moderator: number | null;
	moderator_reply_to_question_id: number | null;
	moderator_reply_to_comment_id: number | null;
	module_id: number;
	moderator_reply_to_question?: StringIndexObject<unknown> | null;
	moderator_reply_to_comment?: StringIndexObject<unknown> | null;
}

export interface DisplayChatMessage {
	name: string;
	message: string;
	messageType: EMessageTypes;
}

export type GeneralEventsMessage =
	| EventsMessageChat
	| EventsMessageQuestion
	| EventsMessageComment;

export type GeneralEventsMessageMap = Map<string, GeneralEventsMessage>;

export interface ChatReducerState {
	directMessages: {
		[key: string]: ChatConvo;
	};
	rooms: ChatRoom[];
	selectedRoom: number | null;
	selectedUser: ConnectedUser | null;
	lastUnseen: number | null; // timestamp to indicate unseen messages was updated
	gr_user: number;
	legacyModeratedComments: LegacyModeratedChatMessages;
	moderatedEventsMessages: GeneralEventsMessageMap;
	shouldViewModerated: boolean;
}
